import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * Detect / investigate / respond - security operations on the one aggregated
 * Codex data model. Addressed to IT operations, security leads AND SOC teams:
 * the value must not read as SOC-only; a SOC (in-house or partner) is one way
 * to build on it, and opsanio is explicitly NOT a finished/24-7 SOC offering.
 *
 * Honest, repo-grounded: detection = Wazuh + the Sigma rule pool + ar-plugin
 * yara/sysmon/exfilwatch; investigation = ar-plugin velociraptor/osquery and
 * the investigate view; response = prioritised recommendations only - opsanio
 * does not patch or execute remediation itself, the customer team acts; hardening = CIS / BSI / DISA-STIG audits;
 * reporting = the asset/event report + permalink dialog. Navy emphasis band
 * (matches hero / final CTA).
 */

const tp = "anketa.autonubil.home.ar";

const Check: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M5 13l4 4L19 7" stroke="var(--ak-accent-2)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const AutonubilOpsanioAR: React.FC = () => {
  const { translate } = useAnketaI18n();

  // One building block per point: a short label plus what the team gets.
  const blocks: [string, string][] = [
    ["Context", "One data model for assets, identities, container images, CVEs, flows and events from 40+ sources"],
    ["Detection", "Wazuh SIEM, 3,000+ Sigma rules, YARA, Sysmon and removable-media monitoring"],
    ["Investigation", "Velociraptor and osquery forensics, cross-source views"],
    ["Recommendation", "Prioritised actions per finding - your team decides and acts"],
    ["Hardening", "Continuous CIS, BSI & DISA-STIG audits"],
    ["Reporting", "Share assets and events as reports or permalinks"],
  ];
  const points = blocks.map(([label, text], i) => ({
    label: translate(`${tp}.label${i + 1}`, { defaultMessage: label } as any),
    text: translate(`${tp}.point${i + 1}`, { defaultMessage: text } as any),
  }));

  return (
    <section
      id="opsanio-ar"
      style={{
        position: "relative",
        background: "var(--ak-hero-bg)",
        color: "var(--ak-on-navy)",
        padding: "clamp(64px, 9vw, 104px) clamp(24px, 5vw, 48px)",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      {/* faint grid, matching the hero band */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          backgroundImage:
            "linear-gradient(var(--ak-hero-grid) 1px, transparent 1px), linear-gradient(90deg, var(--ak-hero-grid) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(120% 90% at 50% 0%, #000 35%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(120% 90% at 50% 0%, #000 35%, transparent 80%)",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 980,
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px",
            borderRadius: 999,
            background: "var(--ak-on-navy-surface-2)",
            border: "1px solid var(--ak-accent-border)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "var(--ak-accent-2)",
          }}
        >
          {translate(`${tp}.eyebrow`, { defaultMessage: "Detect · Investigate · Respond" } as any)}
        </span>

        <h2
          style={{
            margin: "20px 0 0",
            fontSize: "clamp(1.9rem, 3.6vw, 2.9rem)",
            fontWeight: 800,
            letterSpacing: "-0.025em",
            lineHeight: 1.1,
            color: "var(--ak-on-navy)",
          }}
        >
          {translate(`${tp}.title.pre`, { defaultMessage: "From detection to response." } as any)}{" "}
          <span
            style={{
              background: "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {translate(`${tp}.title.highlight`, { defaultMessage: "Working directly on the aggregated data." } as any)}
          </span>
        </h2>

        <p
          style={{
            margin: "18px auto 0",
            maxWidth: 720,
            fontSize: "clamp(1.02rem, 1.6vw, 1.18rem)",
            lineHeight: 1.65,
            color: "var(--ak-on-navy-secondary)",
          }}
        >
          {translate(`${tp}.body`, {
            defaultMessage:
              "IT operations, security leads and SOC analysts all work with the same aggregated data: one model for assets, identities, vulnerabilities, flows and events, with detection, investigation and response built in. Your team gets answers without stitching tools together. If you run a SOC - in-house or with a partner - it builds directly on the same foundation.",
          } as any)}
        </p>

        <ul
          style={{
            listStyle: "none",
            margin: "32px auto 0",
            padding: 0,
            maxWidth: 820,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: "14px 32px",
            textAlign: "left",
          }}
        >
          {points.map((p) => (
            <li
              key={p.label}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                fontSize: "1rem",
                lineHeight: 1.5,
                color: "var(--ak-on-navy)",
                fontWeight: 500,
              }}
            >
              <Check />
              <span>
                <span
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--ak-accent-2)",
                    marginBottom: 2,
                  }}
                >
                  {p.label}
                </span>
                {p.text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default AutonubilOpsanioAR;
